import proj4 from 'proj4';
import { getUTMProjection } from '../../app/components/mepbay/utils/coordinateUtils';

const WGS84_PROJ4 = '+proj=longlat +datum=WGS84 +no_defs';

// WGS84 UTM EPSG codes: 326xx = northern hemisphere, 327xx = southern hemisphere,
const UTM_EPSG_RE = /^EPSG:(326|327)(\d{2})$/i;

/**
 * @function resolveCrsToProj4
 * @description Resolves a CRS identifier (as typed by the user, e.g. "EPSG:32722") into a
 * proj4-understandable definition string. proj4 only knows well-known strings
 * out of the box (EPSG:4326, raw +proj=... strings) arbitrary EPSG codes need
 * their definition registered first, so UTM codes are resolved by parsing the
 * zone/hemisphere out of the code itself, mirroring `getUTMProjection`.
 * @param {string} crs
 * **Called by:
 * @see {requireProj4}
 * @see {parseShoreSModelFile}
 * @memberof module:ShoreS
 * @returns {string|null} A proj4 definition string, or null if unresolvable.
 */
export const resolveCrsToProj4 = (crs) => {
  if (!crs) return null;
  const trimmed = crs.trim();
  if (/^(EPSG:4326|WGS84)$/i.test(trimmed)) return WGS84_PROJ4;

  const match = trimmed.match(UTM_EPSG_RE);
  if (match) {
    const isSouth = match[1] === '327';
    const zone = parseInt(match[2], 10);
    return getUTMProjection(zone, isSouth);
  }

  // Already a raw proj4 definition string (e.g. "+proj=...").
  if (/^\+proj=/i.test(trimmed)) return trimmed;

  return null;
};

const requireProj4 = (crs) => {
  const resolved = resolveCrsToProj4(crs);
  if (!resolved) {
    throw new Error(
      `Não foi possível resolver o CRS "${crs}" para reprojeção. Use um código UTM (EPSG:326xx/327xx), EPSG:4326 ou uma string proj4.`,
    );
  }
  return resolved;
};

/**
 * @function reprojectPoint
 * @description Reprojects a single [x, y] coordinate pair.
 * @param {Array<number>} point - An `[x, y]` pair.
 * @param {string} fromCrs
 * @param {string} toCrs
 * @memberof module:ShoreS
 * @returns {Array<number>} An `[x, y]` pair.
 */
export const reprojectPoint = (point, fromCrs, toCrs) => {
  const fromProj = requireProj4(fromCrs);
  const toProj = requireProj4(toCrs);
  return proj4(fromProj, toProj, point);
};

/**
 * @function reprojectLineString
 * @description Reprojects an array of [x, y] coordinate pairs (e.g. a baseline or transect).
 * @param {Array<Array<number>>} coords - `[x, y]` pairs.
 * @param {string} fromCrs
 * @param {string} toCrs
 * @memberof module:ShoreS
 * @returns {Array<Array<number>>} `[x, y]` pairs.
 */
export const reprojectLineString = (coords, fromCrs, toCrs) => {
  const fromProj = requireProj4(fromCrs);
  const toProj = requireProj4(toCrs);
  return coords.map((coord) => proj4(fromProj, toProj, coord));
};

const reprojectCoordinatesDeep = (coords, project) => {
  if (typeof coords[0] === 'number') return project(coords);
  return coords.map((c) => reprojectCoordinatesDeep(c, project));
};

/**
 * @function reprojectFeatureCollection
 * @description Reprojects every coordinate of every feature in a GeoJSON FeatureCollection.
 * Supports Point/LineString/MultiLineString/Polygon geometries (anything whose
 * `coordinates` is an arbitrarily nested array of [x, y] pairs). Does not mutate
 * the input.
 * @param {GeoJSON.FeatureCollection} featureCollection
 * @param {string} fromCrs
 * @param {string} toCrs
 * @memberof module:ShoreS
 * @returns {GeoJSON.FeatureCollection}
 */
export const reprojectFeatureCollection = (featureCollection, fromCrs, toCrs) => {
  const fromProj = requireProj4(fromCrs);
  const toProj = requireProj4(toCrs);
  const project = (coord) => proj4(fromProj, toProj, coord);

  return {
    ...featureCollection,
    crs: { type: 'name', properties: { name: toCrs } },
    features: (featureCollection.features || []).map((feature) => ({
      ...feature,
      geometry: {
        ...feature.geometry,
        coordinates: reprojectCoordinatesDeep(feature.geometry.coordinates, project),
      },
    })),
  };
};
